import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema } from '../../utils/seo';

export default function PaintersRoyalAscotPage() {
  const [activeTab, setActiveTab] = useState('overview');

  useSEO({
    title: 'Royal Ascot Painters | Professional Painting Services | 21+ Years Experience',
    description: 'Expert painters in Royal Ascot offering premium residential and commercial painting services. Specializing in luxury estates, golf course properties, and high-end homes. Free quotes, quality workmanship, and professional service guaranteed.',
    keywords: 'Royal Ascot painters, painters Royal Ascot, painting services Royal Ascot, house painters Royal Ascot, residential painters, commercial painters, luxury estate painters, golf estate painters, professional painters',
    ogImage: 'https://public.readdy.ai/ai/img_res/1925d33a4421011d3dfd47460314f283.jpg',
    schema: [
      generateLocalBusinessSchema({
        name: 'Painters Royal Ascot',
        description: 'Professional painting services in Royal Ascot specializing in luxury residential estates and golf course properties with over 21 years of experience.',
        address: {
          streetAddress: 'Royal Ascot',
          addressLocality: 'Milnerton',
          addressRegion: 'Western Cape',
          postalCode: '7441',
          addressCountry: 'ZA'
        },
        geo: {
          latitude: '-33.8756',
          longitude: '18.5234'
        },
        areaServed: ['Royal Ascot', 'Milnerton', 'Century City', 'Parklands', 'Table View', 'Sunningdale', 'West Beach'],
        priceRange: '$$$',
        image: 'https://readdy.ai/api/search-image?query=professional%20painting%20company%20team%20working%20on%20luxury%20golf%20estate%20property%20modern%20equipment%20clean%20white%20background%20high%20quality%20professional%20photography&width=800&height=600&seq=royalascot-business&orientation=landscape'
      }),
      generateServiceSchema({
        name: 'Luxury Estate Painting Services',
        description: 'Premium painting services for luxury estates and golf course properties in Royal Ascot',
        provider: 'Painters Royal Ascot',
        areaServed: 'Royal Ascot, Milnerton',
        serviceType: 'Painting Service'
      })
    ]
  });

  const services = [
    {
      title: 'Luxury Estate Painting',
      description: 'Premium painting services for high-end estate homes with sophisticated finishes and attention to detail.',
      icon: 'ri-home-5-line'
    },
    {
      title: 'Golf Course Properties',
      description: 'Specialized painting for golf estate homes maintaining pristine appearance and property value.',
      icon: 'ri-trophy-line'
    },
    {
      title: 'Interior Design Painting',
      description: 'Expert interior painting with designer finishes, custom colors, and premium quality materials.',
      icon: 'ri-paint-brush-line'
    },
    {
      title: 'Exterior Excellence',
      description: 'Durable exterior painting with weather-resistant coatings and meticulous surface preparation.',
      icon: 'ri-shield-check-line'
    },
    {
      title: 'Commercial Properties',
      description: 'Professional painting services for clubhouses, offices, and commercial buildings.',
      icon: 'ri-store-2-line'
    },
    {
      title: 'Maintenance Programs',
      description: 'Comprehensive maintenance painting programs for estates and property portfolios.',
      icon: 'ri-tools-line'
    }
  ];

  const reviews = [
    {
      name: 'Richard Blackwood',
      rating: 5,
      text: 'Exceptional service for our estate home. The team was professional, punctual, and delivered outstanding quality. They understood the high standards required in Royal Ascot and exceeded our expectations.',
      date: '2 weeks ago'
    },
    {
      name: 'Catherine Morrison',
      rating: 5,
      text: 'We hired them for our golf estate property and couldn\'t be happier. The attention to detail is remarkable and the finish is absolutely perfect. Highly recommend for luxury properties!',
      date: '1 month ago'
    },
    {
      name: 'Andrew van der Berg',
      rating: 5,
      text: 'Best painters we\'ve worked with. They transformed our home with beautiful finishes and their expertise in color consultation was invaluable. Professional team from start to finish.',
      date: '1 month ago'
    },
    {
      name: 'Jennifer Steyn',
      rating: 5,
      text: 'Outstanding workmanship! They painted both interior and exterior of our property and the results are stunning. Clean, efficient, and the quality is exceptional.',
      date: '2 months ago'
    },
    {
      name: 'Michael Thompson',
      rating: 5,
      text: 'Excellent service for our clubhouse renovation. They completed the project on time and within budget. The team is reliable, skilled, and a pleasure to work with.',
      date: '2 months ago'
    },
    {
      name: 'Sarah Williams',
      rating: 5,
      text: 'Transformed our estate home beautifully! The painters were respectful, clean, and delivered exceptional quality. Their expertise in luxury painting really shows in the final result.',
      date: '3 months ago'
    }
  ];

  const galleryImages = [
    {
      url: 'https://readdy.ai/api/search-image?query=luxury%20estate%20home%20exterior%20freshly%20painted%20white%20modern%20architecture%20golf%20course%20views%20clean%20simple%20background%20professional%20photography%20bright%20daylight%20premium%20property&width=600&height=400&seq=royalascot-gallery-1&orientation=landscape',
      title: 'Luxury Estate Home'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=elegant%20interior%20living%20room%20freshly%20painted%20neutral%20tones%20luxury%20furniture%20clean%20simple%20background%20professional%20photography%20natural%20lighting%20high%20end%20residential&width=600&height=400&seq=royalascot-gallery-2&orientation=landscape',
      title: 'Interior Living Space'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=golf%20estate%20property%20exterior%20painted%20white%20contemporary%20design%20manicured%20garden%20clean%20simple%20background%20professional%20photography%20bright%20natural%20light&width=600&height=400&seq=royalascot-gallery-3&orientation=landscape',
      title: 'Golf Estate Property'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=clubhouse%20building%20exterior%20freshly%20painted%20modern%20architecture%20clean%20simple%20background%20professional%20photography%20bright%20daylight%20commercial%20property&width=600&height=400&seq=royalascot-gallery-4&orientation=landscape',
      title: 'Clubhouse Building'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=luxury%20master%20bedroom%20interior%20freshly%20painted%20soft%20elegant%20colors%20designer%20furniture%20clean%20simple%20background%20professional%20photography%20natural%20lighting&width=600&height=400&seq=royalascot-gallery-5&orientation=landscape',
      title: 'Master Bedroom'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=modern%20gourmet%20kitchen%20interior%20painted%20white%20and%20grey%20luxury%20finishes%20clean%20simple%20background%20professional%20photography%20bright%20natural%20lighting&width=600&height=400&seq=royalascot-gallery-6&orientation=landscape',
      title: 'Gourmet Kitchen'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-black text-white py-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <Link to="/" className="text-2xl font-bold" style={{ color: '#e9f638' }}>
              Painters Directory
            </Link>
            <div className="hidden md:flex space-x-8">
              <Link to="/" className="hover:text-gray-300 transition-colors whitespace-nowrap">Home</Link>
              <Link to="/about" className="hover:text-gray-300 transition-colors whitespace-nowrap">About</Link>
              <Link to="/contact" className="hover:text-gray-300 transition-colors whitespace-nowrap">Contact</Link>
              <Link to="/get-a-quote" className="px-6 py-2 rounded-lg whitespace-nowrap transition-colors" style={{ backgroundColor: '#e9f638', color: '#000' }}>
                Get a Quote
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative h-96 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=royal%20ascot%20golf%20estate%20luxury%20homes%20modern%20architecture%20golf%20course%20landscape%20clean%20simple%20background%20professional%20photography%20bright%20natural%20lighting%20premium%20residential&width=1920&height=600&seq=royalascot-hero&orientation=landscape"
            alt="Royal Ascot Painters"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        <div className="relative h-full flex items-center justify-center text-center px-4">
          <div className="w-full">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">Painters Royal Ascot</h1>
            <p className="text-xl md:text-2xl text-white mb-6">Premium Painting Services for Luxury Estates</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/get-a-quote"
                className="px-8 py-3 rounded-lg font-semibold transition-all hover:scale-105 whitespace-nowrap"
                style={{ backgroundColor: '#e9f638', color: '#000' }}
              >
                Get Free Quote
              </Link>
              <a
                href="tel:0219876543"
                className="px-8 py-3 bg-white text-black rounded-lg font-semibold transition-all hover:scale-105 whitespace-nowrap"
              >
                Call Now
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="border-b border-gray-200 sticky top-16 bg-white z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8 overflow-x-auto">
            {['overview', 'services', 'gallery', 'reviews'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                  activeTab === tab
                    ? 'border-black text-black'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {activeTab === 'overview' && (
          <div className="space-y-12">
            {/* About Section */}
            <section>
              <h2 className="text-3xl font-bold mb-6">About Painters Royal Ascot</h2>
              <div className="prose max-w-none space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Welcome to Painters Royal Ascot, your premier choice for professional painting services in one of Cape Town's most prestigious golf estate communities. With over 21 years of experience in the painting industry, we have established ourselves as the leading painting contractor for luxury residential estates, golf course properties, and high-end homes in the Royal Ascot area and surrounding premium communities.
                </p>
                <p>
                  Royal Ascot is renowned for its world-class golf course, luxury homes, and exclusive lifestyle that demands the highest standards of workmanship and attention to detail. Our team of expert painters understands the unique requirements of painting in this prestigious estate, from maintaining the aesthetic standards expected by discerning property owners to working within the estate's guidelines and regulations. We take pride in delivering exceptional results that enhance the beauty and value of your property while maintaining the pristine appearance that Royal Ascot is known for.
                </p>
                <p>
                  Our company was founded on the principles of quality craftsmanship, professional service, and unwavering commitment to excellence. Over the years, we have built a reputation as the preferred painting contractor for luxury estates by consistently delivering outstanding results on every project, whether it's a new home painting, estate renovation, golf course property maintenance, or commercial clubhouse upgrade. We understand that your property represents a significant investment and a reflection of your lifestyle, and we treat every project with the care, respect, and attention it deserves.
                </p>
                <p>
                  What sets Painters Royal Ascot apart from other painting contractors is our specialized expertise in luxury estate painting, our commitment to using only premium quality paints and materials, and our team of highly skilled painters who are experienced in working with high-end properties. We work closely with homeowners, estate managers, interior designers, and architects to ensure that every aspect of the painting project meets or exceeds the exacting standards expected in Royal Ascot. From initial color consultation to final inspection, we maintain the highest levels of professionalism and craftsmanship.
                </p>
                <p>
                  Our expertise extends across all types of painting projects, including interior painting, exterior painting, specialty finishes, decorative painting, protective coatings, and commercial painting. We are particularly experienced in working with luxury properties that require sophisticated color schemes, premium finishes, and expert surface preparation. Whether you're looking to refresh a single room, repaint your entire estate home, maintain a golf course property, or upgrade a commercial facility, we have the skills, experience, and resources to deliver exceptional results that exceed your expectations.
                </p>
                <p>
                  We understand that painting projects in an exclusive estate environment require special consideration for neighbors, estate regulations, and minimal disruption. That's why we work efficiently and professionally to minimize inconvenience while maintaining our high standards of quality. Our team is fully insured, professionally trained, and committed to maintaining a clean and safe work environment. We use dust-free sanding equipment, protective coverings, and proper ventilation to ensure that your property remains clean and comfortable throughout the painting process.
                </p>
                <p>
                  At Painters Royal Ascot, we believe in building long-term relationships with our clients based on trust, reliability, and exceptional service. Many of our clients are repeat customers who return to us for all their painting needs and recommend us to their neighbors and friends within the estate. This word-of-mouth reputation within the Royal Ascot community is something we value highly and work hard to maintain with every project we undertake. We're proud to be the trusted painting partner for many of Royal Ascot's most beautiful homes.
                </p>
                <p>
                  Our service area includes Royal Ascot, Milnerton, Century City, Parklands, Table View, Sunningdale, West Beach, and surrounding premium areas. We are familiar with the local architecture, estate regulations, and property standards, which allows us to provide expert advice and recommendations tailored to your specific needs. Whether you own an estate home, golf course property, or commercial facility, we have the expertise to deliver results that enhance your property's beauty and value while meeting the high standards expected in this exclusive community.
                </p>
              </div>
            </section>

            {/* Why Choose Us */}
            <section className="bg-gray-50 rounded-lg p-8">
              <h2 className="text-3xl font-bold mb-6">Why Choose Painters Royal Ascot?</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-medal-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">21+ Years Experience</h3>
                    <p className="text-gray-600">Extensive experience in luxury estate and golf course property painting throughout Royal Ascot and Cape Town.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-vip-crown-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Luxury Specialists</h3>
                    <p className="text-gray-600">Specialized expertise in high-end estate properties with sophisticated finishes and premium materials.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-team-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Expert Team</h3>
                    <p className="text-gray-600">Highly skilled painters with specialized training in luxury property painting and finishing techniques.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-shield-check-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Fully Insured</h3>
                    <p className="text-gray-600">Comprehensive insurance coverage for your peace of mind and property protection.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-star-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Premium Quality</h3>
                    <p className="text-gray-600">We use only the finest quality paints and materials to ensure lasting beauty and durability.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-customer-service-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Exceptional Service</h3>
                    <p className="text-gray-600">Professional, courteous service from initial consultation through project completion and beyond.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Service Areas */}
            <section>
              <h2 className="text-3xl font-bold mb-6">Service Areas</h2>
              <p className="text-gray-700 mb-4">
                We proudly serve Royal Ascot and surrounding premium areas including:
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {['Royal Ascot', 'Milnerton', 'Century City', 'Parklands', 'Table View', 'Sunningdale', 'West Beach', 'Bloubergstrand'].map((area) => (
                  <div key={area} className="flex items-center space-x-2">
                    <i className="ri-map-pin-line" style={{ color: '#e9f638' }}></i>
                    <span className="text-gray-700">{area}</span>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}

        {activeTab === 'services' && (
          <div className="space-y-12">
            <section>
              <h2 className="text-3xl font-bold mb-6">Our Services</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {services.map((service, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                    <div className="w-12 h-12 flex items-center justify-center rounded-lg mb-4" style={{ backgroundColor: '#e9f638' }}>
                      <i className={`${service.icon} text-2xl`}></i>
                    </div>
                    <h3 className="text-xl font-semibold mb-3">{service.title}</h3>
                    <p className="text-gray-600">{service.description}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Detailed Services */}
            <section className="prose max-w-none">
              <h2 className="text-3xl font-bold mb-6">Comprehensive Painting Solutions</h2>
              <div className="space-y-6 text-gray-700">
                <div>
                  <h3 className="text-2xl font-semibold mb-3">Luxury Estate Painting</h3>
                  <p>
                    Our luxury estate painting services are designed specifically for high-end residential properties in Royal Ascot and surrounding premium communities. We understand that luxury estates require exceptional attention to detail, premium materials, and expert craftsmanship. Our team specializes in creating beautiful, sophisticated finishes that enhance the architectural features of your estate home while reflecting your personal style and maintaining the prestigious standards of the community. From elegant neutral palettes to bold designer colors, we work with you to achieve the perfect look for your luxury property.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-semibold mb-3">Golf Course Property Painting</h3>
                  <p>
                    Properties overlooking the golf course require special attention to maintain their pristine appearance and property value. Our golf course property painting services include comprehensive exterior and interior painting using premium materials designed to withstand the elements while maintaining beautiful aesthetics. We understand the importance of maintaining the visual harmony of properties within the golf estate and work to ensure your home complements the stunning natural surroundings while standing out for its impeccable finish and attention to detail.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-semibold mb-3">Interior Design Painting</h3>
                  <p>
                    Transform your interior spaces with our specialized interior design painting services. We offer expert color consultation, custom color matching, specialty finishes, feature walls, and decorative painting techniques. Our painters are experienced in working with interior designers and architects to bring design visions to life with precision and artistry. Whether you're looking for subtle elegance, dramatic impact, or contemporary sophistication, we have the skills and expertise to deliver exceptional results that exceed your expectations and enhance your living spaces.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-semibold mb-3">Estate Maintenance Programs</h3>
                  <p>
                    Maintaining the pristine appearance of your estate property requires ongoing attention and care. Our comprehensive maintenance painting programs are designed to keep your property looking beautiful year-round while protecting your investment. We offer scheduled maintenance services, touch-up painting, seasonal inspections, and preventive maintenance to address issues before they become problems. Our maintenance programs are customized to your property's specific needs and can include both interior and exterior painting services to ensure your estate home always looks its absolute best.
                  </p>
                </div>
              </div>
            </section>
          </div>
        )}

        {activeTab === 'gallery' && (
          <div>
            <h2 className="text-3xl font-bold mb-6">Our Work</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {galleryImages.map((image, index) => (
                <div key={index} className="group relative overflow-hidden rounded-lg aspect-video">
                  <img
                    src={image.url}
                    alt={image.title}
                    className="w-full h-full object-cover object-top transition-transform group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <p className="text-white font-semibold text-lg">{image.title}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'reviews' && (
          <div>
            <h2 className="text-3xl font-bold mb-6">Customer Reviews</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {reviews.map((review, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg">{review.name}</h3>
                      <p className="text-sm text-gray-500">{review.date}</p>
                    </div>
                    <div className="flex space-x-1">
                      {[...Array(review.rating)].map((_, i) => (
                        <i key={i} className="ri-star-fill" style={{ color: '#e9f638' }}></i>
                      ))}
                    </div>
                  </div>
                  <p className="text-gray-700">{review.text}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* CTA Section */}
      <section className="py-16" style={{ backgroundColor: '#e9f638' }}>
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-4xl font-bold mb-4">Ready to Transform Your Estate?</h2>
          <p className="text-xl mb-8">Get a free, no-obligation quote for your painting project today.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/get-a-quote"
              className="px-8 py-3 bg-black text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors whitespace-nowrap"
            >
              Request Free Quote
            </Link>
            <a
              href="tel:0219876543"
              className="px-8 py-3 bg-white text-black rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              Call Us Now
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold mb-4" style={{ color: '#e9f638' }}>Painters Royal Ascot</h3>
              <p className="text-gray-400">Professional painting services for luxury estates in Royal Ascot and surrounding areas.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-white transition-colors">Home</Link></li>
                <li><Link to="/about" className="text-gray-400 hover:text-white transition-colors">About</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white transition-colors">Contact</Link></li>
                <li><Link to="/get-a-quote" className="text-gray-400 hover:text-white transition-colors">Get a Quote</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Luxury Estate Painting</li>
                <li>Golf Course Properties</li>
                <li>Interior Design Painting</li>
                <li>Maintenance Programs</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-center space-x-2">
                  <i className="ri-phone-line"></i>
                  <span>021 987 6543</span>
                </li>
                <li className="flex items-center space-x-2">
                  <i className="ri-map-pin-line"></i>
                  <span>Royal Ascot, Milnerton</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm mb-4 md:mb-0">
              © 2024 Painters Royal Ascot. All rights reserved.
            </p>
            <div className="flex space-x-6 text-sm text-gray-400">
              <a href="https://leaderr.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                Powered by leaderr
              </a>
              <a href="https://seostudio.co.za" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                SEO by SEO Studio
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}